import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Signpost, ShieldCheck, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { setPageMeta } from '../lib/seo';

// Request a Sign (Phase 123). One form, one admin list. The Contact form's
// "Request a Sign" box files into the same list, so nothing is duplicated.
export default function RequestSign() {
  const [opts, setOpts] = useState({ sizes: [], intro: '', privacy_note: '' });
  const [form, setForm] = useState({ name: '', address: '', email: '', phone: '', size: 'either', notes: '', permission: false, website: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get('/sign-requests/options').then((r) => setOpts(r.data)).catch(() => {});
    setPageMeta({ title: 'Request a Lawn Sign | Steve Lesperance, Ward 8', description: 'Request a free Vote Steve Lesperance lawn sign for your Ward 8 property. A volunteer places it and picks it up after election day.', url: window.location.origin + '/request-a-sign' });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = 'Please enter your name.';
    if (form.address.trim().length < 5) e.address = 'Please enter the address where the sign should go.';
    if (!form.email.trim() && !form.phone.trim()) e.contact = 'Give a phone number or an email address (at least one).';
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = 'That email address does not look right.';
    if (form.phone.trim() && form.phone.replace(/\D/g, '').length < 10) e.phone = 'Please enter a 10 digit phone number.';
    if (!form.permission) e.permission = 'Please confirm you have permission to place a sign at this address.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Please check the highlighted fields.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/sign-requests', form);
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your request right now. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="request-sign-page">
      <PageHeader pageKey="request-a-sign" kicker="SHOW YOUR SUPPORT" title="Request a Lawn Sign" subtitle="Free. A volunteer places it and picks it up after election day. Tell us where it goes." image={fileUrl('/api/uploads/vote-steve-hero.webp')} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {done ? (
            <Card className="border-emerald-200 bg-emerald-50" data-testid="request-sign-success"><CardContent className="p-6 sm:p-8">
              <p className="font-black text-emerald-900 text-xl inline-flex items-center gap-2"><CheckCircle2 className="h-6 w-6" /> {done.duplicate ? 'Already on the list' : 'Sign request received'}</p>
              <p className="mt-2 text-emerald-900/90">{done.message}</p>
              <p className="mt-2 text-sm text-emerald-900/70">Reference <strong data-testid="request-sign-reference">{done.reference}</strong>. Keep it in case you need to reach the team.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="request-sign-volunteer-link"><Link to="/volunteer">Want to do more? Volunteer</Link></Button>
                <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold bg-white btn-press" data-testid="request-sign-home-link"><Link to="/home">Back to Home</Link></Button>
              </div>
            </CardContent></Card>
          ) : (
            <form onSubmit={submit} noValidate data-testid="request-sign-form">
              <Card><CardContent className="p-5 sm:p-7 space-y-5">
                {opts.intro && <p className="text-slate-700" data-testid="request-sign-intro">{opts.intro}</p>}
                <div>
                  <Label htmlFor="rs-name" className="font-bold text-[#1a2744]">Your name *</Label>
                  <Input id="rs-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="h-12 mt-1" autoComplete="name" aria-invalid={!!errors.name} data-testid="request-sign-name" />
                  {errors.name && <p className="text-sm text-red-700 mt-1" data-testid="request-sign-error-name">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="rs-address" className="font-bold text-[#1a2744] inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Where should the sign go? *</Label>
                  <Input id="rs-address" value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Street address and community (for example 12 Main St, Omemee)" className="h-12 mt-1" autoComplete="street-address" aria-invalid={!!errors.address} data-testid="request-sign-address" />
                  {errors.address && <p className="text-sm text-red-700 mt-1" data-testid="request-sign-error-address">{errors.address}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rs-phone" className="font-bold text-[#1a2744]">Phone</Label>
                    <Input id="rs-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="h-12 mt-1" autoComplete="tel" aria-invalid={!!errors.phone} data-testid="request-sign-phone" />
                    {errors.phone && <p className="text-sm text-red-700 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label htmlFor="rs-email" className="font-bold text-[#1a2744]">Email</Label>
                    <Input id="rs-email" type="email" inputMode="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="h-12 mt-1" autoComplete="email" aria-invalid={!!errors.email} data-testid="request-sign-email" />
                    {errors.email && <p className="text-sm text-red-700 mt-1">{errors.email}</p>}
                  </div>
                </div>
                <p className={`text-sm -mt-2 ${errors.contact ? 'text-red-700' : 'text-slate-500'}`} data-testid="request-sign-contact-hint">{errors.contact || 'At least one: phone or email, so a volunteer can reach you.'}</p>
                <div>
                  <p className="font-bold text-[#1a2744] mb-2">Sign size</p>
                  <RadioGroup value={form.size} onValueChange={(v) => set('size', v)} className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="request-sign-sizes">
                    {(opts.sizes || []).map((s) => (
                      <label key={s.key} htmlFor={`rs-size-${s.key}`} className={`flex items-center gap-2 rounded-lg border px-3 py-3 min-h-[48px] cursor-pointer ${form.size === s.key ? 'border-[#1a2744] bg-[#f3f7ff]' : 'border-slate-300 bg-white'}`}>
                        <RadioGroupItem id={`rs-size-${s.key}`} value={s.key} data-testid={`request-sign-size-${s.key}`} />
                        <span className="text-sm font-semibold text-[#1a2744]">{s.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label htmlFor="rs-notes" className="font-bold text-[#1a2744]">Notes (optional)</Label>
                  <Textarea id="rs-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="Best spot on the property, gate code, best time to call" className="mt-1" data-testid="request-sign-notes" />
                </div>
                <div className={`flex items-start gap-3 rounded-lg border p-3 ${errors.permission ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <Checkbox id="rs-permission" checked={form.permission} onCheckedChange={(v) => set('permission', !!v)} className="mt-0.5" data-testid="request-sign-permission" />
                  <Label htmlFor="rs-permission" className="text-sm text-slate-800 leading-snug font-normal">I confirm I own this property or have the owner's permission to place a campaign sign here. *</Label>
                </div>
                {errors.permission && <p className="text-sm text-red-700 -mt-3" data-testid="request-sign-error-permission">{errors.permission}</p>}
                <input type="text" name="website" value={form.website} onChange={(e) => set('website', e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <Button type="submit" disabled={busy} className="h-12 w-full sm:w-auto px-8 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold btn-press" data-testid="request-sign-submit">
                  {busy ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Sending</> : <><Signpost className="h-5 w-5 mr-2" /> Request My Sign</>}
                </Button>
              </CardContent></Card>
            </form>
          )}
        </div>
        <aside className="space-y-4">
          <Card><CardContent className="p-5">
            <img src={fileUrl('/api/uploads/yard-sign-18x24.png')} alt="Campaign yard sign artwork, PITA design, 18 by 24 inches" className="rounded-lg border border-slate-200 w-full" loading="lazy" data-testid="request-sign-preview-image" />
            <p className="text-sm text-slate-600 mt-3">Current campaign yard sign artwork (18 x 24 in). Larger 24 x 18 in roadside signs are available for high traffic spots. Final printed designs may vary.</p>
          </CardContent></Card>
          <Card className="bg-[#f3f7ff] border-[#c7d7f5]"><CardContent className="p-5">
            <p className="font-bold text-[#1a2744] inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Your privacy</p>
            <p className="text-sm text-slate-700 mt-1.5" data-testid="request-sign-privacy">{opts.privacy_note || 'Your address and contact details are used only to place and collect the sign. They are never published.'}</p>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <p className="font-bold text-[#1a2744]">Other ways to help</p>
            <ul className="mt-2 space-y-1.5 text-sm">
              <li><Link to="/volunteer" className="font-semibold text-[#24406f] underline underline-offset-2" data-testid="request-sign-aside-volunteer">Volunteer</Link></li>
              <li><Link to="/donate" className="font-semibold text-[#24406f] underline underline-offset-2" data-testid="request-sign-aside-donate">Donate</Link></li>
              <li><Link to="/about/cv-flyer" className="font-semibold text-[#24406f] underline underline-offset-2" data-testid="request-sign-aside-flyer">Download and share the flyer</Link></li>
            </ul>
          </CardContent></Card>
        </aside>
      </div>
    </div>
  );
}
